import { useEffect, useState } from "react";
import type { Map as LeafletMap } from "leaflet";
import {
  NENDO_PHOTO_MIN_ZOOM,
  NENDO_PHOTO_YEARS,
  getNendoPhotoTileUrl,
} from "@/features/map/constants/gsiPhotoLayers";
import {
  latLngToTileCoords,
  probeTileAvailability,
} from "@/features/map/utils/tileProbe";
import { tileKey } from "@/features/map/utils/tileCoverage";

/** 年度 → 地図中心付近に写真があるか */
export type NendoPhotoYearAvailability = Record<number, boolean>;

interface Params {
  map: LeafletMap | null;
  /** 年度リストを開いている間だけ true にし、無駄なリクエストを避ける */
  enabled: boolean;
}

/** 地図移動が落ち着くのを待つ時間（ms）。連続した moveend を 1 回にまとめる */
const DEBOUNCE_MS = 1000;

/**
 * 確認に使うタイルのズーム。配信最小ズーム（14、約 2.4km 四方）に固定し、
 * 高ズームでの小さな移動では同じタイルに留まるようにして外部へのリクエストを抑える。
 */
const PROBE_ZOOM = NENDO_PHOTO_MIN_ZOOM;

/** 確認済みタイルの結果。セッション中は同じタイルへ戻っても再リクエストしない */
const availabilityCache = new Map<string, NendoPhotoYearAvailability>();
/** キャッシュの上限。超えたら古いものから捨てる */
const CACHE_LIMIT = 200;

const rememberAvailability = (
  key: string,
  availability: NendoPhotoYearAvailability
): void => {
  if (availabilityCache.size >= CACHE_LIMIT) {
    // Map は挿入順を保つので、先頭のキーが最も古い
    const [oldest] = availabilityCache.keys();
    if (oldest !== undefined) availabilityCache.delete(oldest);
  }
  availabilityCache.set(key, availability);
};

/** テスト用: 確認済みタイルのキャッシュを空にする */
export const clearNendoPhotoYearAvailabilityCache = (): void => {
  availabilityCache.clear();
};

/**
 * 年度別空中写真について、地図中心のタイル 1 枚を年度ごとに読み込み、
 * どの年度に写真があるかを調べる。
 *
 * 地理院は撮影範囲を年度ごとに分かりやすく配信していないため、
 * ユーザーが 20 年度を順に試さなくても済むように、中心付近の有無だけをサンプリングする。
 * 外部（地理院）へのリクエストを抑えるため、
 * - 年度リストを開いている間だけ動く
 * - 判定タイルは ZL14 固定で、中心がそのタイルから出た時だけ確認する
 * - 確認済みタイルの結果はセッション中キャッシュする
 * ので、1 タイルあたり最大 20 リクエスト（数十 KB ずつ）で済む。
 */
export const useNendoPhotoYearAvailability = ({
  map,
  enabled,
}: Params): NendoPhotoYearAvailability | null => {
  const [availability, setAvailability] =
    useState<NendoPhotoYearAvailability | null>(null);

  useEffect(() => {
    if (!enabled || !map) {
      setAvailability(null);
      return;
    }

    let requestId = 0;
    let timer: number | undefined;
    // 直前に確認（またはキャッシュ表示）した中心タイル。同じタイル内の移動では何もしない
    let currentTileKey: string | null = null;

    const probe = (): void => {
      const center = map.getCenter();
      const coords = latLngToTileCoords(center.lat, center.lng, PROBE_ZOOM);
      const key = tileKey(coords);
      if (key === currentTileKey) return;
      currentTileKey = key;
      // 進行中の確認があれば、その結果は捨てる
      requestId += 1;

      const cached = availabilityCache.get(key);
      if (cached) {
        setAvailability(cached);
        return;
      }

      const currentRequest = requestId;
      void Promise.all(
        NENDO_PHOTO_YEARS.map(async (year) => {
          const url = getNendoPhotoTileUrl(year)
            .replace("{z}", String(coords.z))
            .replace("{x}", String(coords.x))
            .replace("{y}", String(coords.y));
          return [year, await probeTileAvailability(url)] as const;
        })
      ).then((entries) => {
        // null（応答がなく打ち切った年度）はキーごと落とし、UI 上は未確認のままにする
        const resolved = entries.filter(
          (entry): entry is readonly [number, boolean] => entry[1] !== null
        );
        const result: NendoPhotoYearAvailability = Object.fromEntries(resolved);
        // 判定できなかった年度がある結果はキャッシュしない
        // （一時的な通信失敗をセッション中ずっと引きずらないため）
        if (resolved.length === entries.length)
          rememberAvailability(key, result);
        else currentTileKey = null;
        // 地図がさらに動いて別のタイルの確認が始まっていたら、古い結果は表示しない
        if (currentRequest !== requestId) return;
        setAvailability(result);
      });
    };

    const scheduleProbe = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(probe, DEBOUNCE_MS);
    };

    probe();
    map.on("moveend", scheduleProbe);
    map.on("zoomend", scheduleProbe);
    return () => {
      window.clearTimeout(timer);
      requestId += 1;
      map.off("moveend", scheduleProbe);
      map.off("zoomend", scheduleProbe);
    };
  }, [map, enabled]);

  return availability;
};
